// A room's features in ONE vocabulary. The selection names a room's feature by
// its kind (`FeatureKind`, selection.ts: a bench, a pane, a partition, a bench
// support, …); a section view and the 3D view name it by the part that shows it
// (`SecPart`, section.ts: a pane is a `pane`, a partition a `partition`, …).
// `FEATURES` is the one table between the two, a Record over the kind: the part
// that shows each, when a click may take it, whether it is selected with its
// room, how a pick selects it and whether its room still holds it. Being a
// Record, a new kind does not compile until it says all of that.
// `liveFeature` is THE resolution of the selected feature against its room (null
// when stale): what the section views box (`featureTargets`), what Tab starts
// from (`featureRefs`), whose handles show — and what `reconcileSelection` keeps.
// DOM-free.

import type { ProductInstance } from '../shared/types';
import type {
  FeatureKind,
  FeatureSel,
  SelectionFields,
} from './selection';
import type {
  SecPart,
  SecSrc,
} from './section';
import { benchLegOf } from './roomBench';

/** A section part that is a room's feature (a drawn wall's is `wallFeature`). */
export type RoomPart = Exclude<SecPart['kind'], 'wallFeature'>;

/** A room's feature by kind, id and room — what a pick takes; a pick stack's
 *  `ObjRef` of a feature (pickStack.ts) has this shape. */
export interface FeatureRef {
  kind: FeatureKind;
  id: string;
  instId: string;
  /** A bench support's run (the row it stands in). */
  run?: number;
}

type FeatureOf<K extends FeatureKind> = Extract<FeatureSel, { kind: K }>;

interface FeatureRule<K extends FeatureKind> {
  /** The section (and 3D) part that shows it. */
  part: RoomPart;
  /** A click takes it only once its room is selected — it sits in what selects
   *  the room (a door, a pane, an opening) or under what a click takes first (a
   *  support under its bench) —; else at once. */
  onRoom: boolean;
  /** It may be selected without its room (a bench); every other feature is
   *  selected with its room. */
  alone: boolean;
  /** Whether the room holds feature `id`. */
  has(room: ProductInstance, id: string): boolean;
  /** The selection a pick of it makes. */
  select(r: FeatureRef): FeatureOf<K>;
  /** The ids it selects, the primary last (several panes or openings; else one). */
  ids(f: FeatureOf<K>): string[];
}

const holds = (list: ReadonlyArray<{ id?: string | null }> | undefined, id: string): boolean => !!list?.some((x) => x.id === id);
/** The primary last, each id once. */
const primaryLast = (ids: readonly string[], id: string | null): string[] => (id ? [...ids.filter((x) => x !== id), id] : [...ids]);

export const FEATURES: { [K in FeatureKind]: FeatureRule<K> } = {
  bench: {
    part: 'bench', onRoom: false, alone: true,
    has: (room, id) => !!benchLegOf(room, id),
    select: (r) => ({ kind: 'bench', instId: r.instId, benchId: r.id }),
    ids: (f) => [f.benchId],
  },
  door: {
    part: 'door', onRoom: true, alone: false,
    has: (room, id) => holds(room.doors, id) || room.door?.id === id,
    select: (r) => ({ kind: 'door', id: r.id }),
    ids: (f) => [f.id],
  },
  fixture: {
    part: 'fixture', onRoom: false, alone: false,
    has: (room, id) => holds(room.backrests, id) || holds(room.lights, id) || holds(room.sculptedBenches, id),
    select: (r) => ({ kind: 'fixture', instId: r.instId, id: r.id }),
    ids: (f) => [f.id],
  },
  glass: {
    part: 'pane', onRoom: true, alone: false,
    has: (room, id) => holds(room.glass, id),
    select: (r) => ({ kind: 'glass', id: r.id, ids: [r.id] }),
    ids: (f) => primaryLast(f.ids, f.id),
  },
  heaterWall: {
    part: 'heaterWall', onRoom: false, alone: false,
    has: (room, id) => holds(room.heaterWalls, id),
    select: (r) => ({ kind: 'heaterWall', instId: r.instId, wallId: r.id }),
    ids: (f) => [f.wallId],
  },
  innerWall: {
    part: 'partition', onRoom: false, alone: false,
    has: (room, id) => holds(room.innerWalls, id),
    select: (r) => ({ kind: 'innerWall', instId: r.instId, wallId: r.id }),
    ids: (f) => [f.wallId],
  },
  opening: {
    part: 'opening', onRoom: true, alone: false,
    has: (room, id) => holds(room.openings, id),
    select: (r) => ({ kind: 'opening', id: r.id, ids: [r.id] }),
    ids: (f) => primaryLast(f.ids, f.id),
  },
  support: {
    part: 'support', onRoom: true, alone: false,
    has: (room, id) => holds(room.benchSupports, id),
    select: (r) => ({ kind: 'support', instId: r.instId, id: r.id, ...(r.run != null ? { run: r.run } : {}) }),
    ids: (f) => [f.id],
  },
};

/** The feature kind each room part shows: the reverse of `FEATURES[kind].part`. */
export const PART_FEATURES: Record<RoomPart, FeatureKind> = {
  bench: 'bench',
  door: 'door',
  fixture: 'fixture',
  heaterWall: 'heaterWall',
  opening: 'opening',
  pane: 'glass',
  partition: 'innerWall',
  support: 'support',
};

/** Whether a section part is a room's feature. */
export function isRoomPart(kind: SecPart['kind']): kind is RoomPart {
  return kind in PART_FEATURES;
}

/** A kind's rule, for a feature whose kind is known only at run time. */
const ruleOf = (kind: FeatureKind): FeatureRule<FeatureKind> => FEATURES[kind] as FeatureRule<FeatureKind>;

/** The room of the selected feature: the one it names, else (a door, panes,
 *  openings) the selected room. */
export function featureRoomId(s: Pick<SelectionFields, 'feature' | 'selectedId'>): string | null {
  const f = s.feature;
  if (!f) return null;
  return 'instId' in f ? f.instId : s.selectedId;
}

/** The selected feature resolved against its room. */
export interface LiveFeature {
  sel: FeatureSel;
  room: ProductInstance;
  /** Its ids the room holds, the primary last. */
  ids: string[];
}

/** The selected feature checked against its room: the room exists and holds it
 *  (its primary, and those of its ids that it still holds), and the room is the
 *  one selected — or, for a bench picked alone, no room is. Null when stale. */
export function liveFeature(s: Pick<SelectionFields, 'feature' | 'selectedId'>, instances: readonly ProductInstance[]): LiveFeature | null {
  const f = s.feature, roomId = featureRoomId(s);
  if (!f || !roomId) return null;
  const rule = ruleOf(f.kind);
  if (s.selectedId !== roomId && !(rule.alone && s.selectedId == null)) return null;
  const room = instances.find((i) => i.id === roomId);
  if (!room) return null;
  const all = rule.ids(f), primary = all[all.length - 1];
  if (!primary || !rule.has(room, primary)) return null;
  return { sel: f, room, ids: all.filter((id) => rule.has(room, id)) };
}

/** The selected feature, live (`liveFeature`), when it is a `kind`. */
export function liveFeatureOf<K extends FeatureKind>(s: Pick<SelectionFields, 'feature' | 'selectedId'>, instances: readonly ProductInstance[], kind: K): (LiveFeature & { sel: FeatureOf<K> }) | null {
  const f = liveFeature(s, instances);
  return f?.sel.kind === kind ? (f as LiveFeature & { sel: FeatureOf<K> }) : null;
}

/** The section part that shows a feature. */
export function featureTarget(r: FeatureRef): SecSrc {
  return { id: r.instId, part: { kind: FEATURES[r.kind].part, id: r.id } };
}

/** What the section views box (and the 3D view outlines) for a live feature:
 *  one target per id. */
export function featureTargets(f: LiveFeature): SecSrc[] {
  return featureRefs(f).map(featureTarget);
}

/** A live feature as picks (what Tab starts from), one per id. */
export function featureRefs(f: LiveFeature | null): FeatureRef[] {
  if (!f) return [];
  const run = f.sel.kind === 'support' ? f.sel.run : undefined;
  return f.ids.map((id) => ({ kind: f.sel.kind, id, instId: f.room.id, ...(run != null ? { run } : {}) }));
}

/** The selection a pick of a feature makes (its room, unless it is `alone`, is
 *  the caller's to select). */
export function featureSel(r: FeatureRef): FeatureSel {
  return ruleOf(r.kind).select(r);
}

/** What a click on a room's section part takes, by the plan's rules: the feature
 *  itself — at once, or once its room is selected (`onRoom`) —, else the bench a
 *  support stands under (`SecPart.bench`), else null: the room takes it. */
export function partPick(roomId: string, part: SecPart, roomSelected: boolean): FeatureRef | null {
  if (!isRoomPart(part.kind)) return null;
  const kind = PART_FEATURES[part.kind];
  if (!FEATURES[kind].onRoom || roomSelected) return { kind, id: part.id, instId: roomId };
  return part.bench ? { kind: 'bench', id: part.bench, instId: roomId } : null;
}
